import { html, View } from 'rune-ts';
import klass from './BadgeSubView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import { CardProductError } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductView.ts';

export type TProps =
  | {
      type: 'Free_Shipping';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Time_left';
      size: 'Large (Xlarge~Large)';
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Stock_left';
      size: 'Large (Xlarge~Large)';
      displayAvailableStock: number;
    }
  | {
      type: 'Popular';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Limited';
      size: 'Large (Xlarge~Large)';
      quantity: number;
    }
  | {
      type: 'Trend';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Free_Shipping';
      size: 'Medium (Medium~Small)';
    }
  | {
      type: 'Time_left';
      size: 'Medium (Medium~Small)';
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Stock_left';
      size: 'Medium (Medium~Small)';
      displayAvailableStock: number;
    }
  | {
      type: 'Popular';
      size: 'Medium (Medium~Small)';
    }
  | {
      type: 'Limited';
      size: 'Medium (Medium~Small)';
      quantity: number;
    }
  | {
      type: 'Trend';
      size: 'Medium (Medium~Small)';
    };

export type TBadgeSubWithoutSize =
  | {
      type: 'Free_Shipping';
    }
  | {
      type: 'Time_left';
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Stock_left';
      displayAvailableStock: number;
    }
  | {
      type: 'Popular';
    }
  | {
      type: 'Limited';
      quantity: number;
    }
  | {
      type: 'Trend';
    };

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=26351-75881&m=dev
 */
export class BadgeSubView extends View<TProps> {
  protected override template(props: TProps) {
    if (props.type === 'Free_Shipping' && props.size === 'Large (Xlarge~Large)') {
      return html`<span class="${classes(klass.type_free_shipping, klass.size_large, font('12_semibold'))}">
        ${ET('card_product::badge_sub::free_shipping')}
      </span>`;
    } else if (props.type === 'Time_left' && props.size === 'Large (Xlarge~Large)') {
      const { sell_end_at, currentTime } = props;

      const days = differenceInDays(new Date(sell_end_at), new Date(currentTime));

      const hours = differenceInHours(new Date(sell_end_at), new Date(currentTime));

      const minutes = differenceInMinutes(new Date(sell_end_at), new Date(currentTime));

      let displayTime;
      if (days >= 1) {
        displayTime = ET('card_product::badge_sub::time_left_days', { days });
      } else if (hours >= 1) {
        displayTime = ET('card_product::badge_sub::time_left_hours', { hours });
      } else if (minutes >= 1) {
        displayTime = ET('card_product::badge_sub::time_left_minutes', { minutes });
      } else {
        displayTime = ET('card_product::badge_sub::time_left_minutes', { minutes: 1 });
      }

      return html`<span class="${classes(klass.type_time_left, klass.size_large, font('12_semibold'))}">
        <span class="${klass.count}">${html.preventEscape(displayTime)}</span>
      </span>`;
    } else if (props.type === 'Stock_left' && props.size === 'Large (Xlarge~Large)') {
      const { displayAvailableStock } = props;

      return html`<span
        class="${classes(klass.type_stock_left, klass.size_large, klass.broken_word, font('12_semibold'))}"
      >
        ${ET('card_product::badge_sub::stock_left', { count: displayAvailableStock })}
      </span>`;
    } else if (props.type === 'Popular' && props.size === 'Large (Xlarge~Large)') {
      return html`<span
        class="${classes(klass.type_popular, klass.size_large, klass.broken_word, font('12_semibold'))}"
      >
        ${ET('card_product::badge_sub::popular')}
      </span>`;
    } else if (props.type === 'Limited' && props.size === 'Large (Xlarge~Large)') {
      const { quantity } = props;

      return html`<span
        class="${classes(klass.type_limited, klass.size_large, klass.broken_word, font('12_semibold'))}"
      >
        ${ET('card_product::badge_sub::limited', { quantity })}
      </span>`;
    } else if (props.type === 'Trend' && props.size === 'Large (Xlarge~Large)') {
      return html`<span
        class="${classes(klass.type_trend, klass.size_large, klass.broken_word, font('12_semibold'))}"
      >
        ${ET('card_product::badge_sub::trend')}
      </span>`;
    } else if (props.type === 'Free_Shipping' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.type_free_shipping, klass.size_medium, font('10_semibold'))}">
        <span>${ET('card_product::badge_sub::free_shipping')}</span>
      </span>`;
    } else if (props.type === 'Time_left' && props.size === 'Medium (Medium~Small)') {
      const { sell_end_at, currentTime } = props;

      const days = differenceInDays(new Date(sell_end_at), new Date(currentTime));

      const hours = differenceInHours(new Date(sell_end_at), new Date(currentTime));

      const minutes = differenceInMinutes(new Date(sell_end_at), new Date(currentTime));

      let displayTime;
      if (days >= 1) {
        displayTime = ET('card_product::badge_sub::time_left_days', { days });
      } else if (hours >= 1) {
        displayTime = ET('card_product::badge_sub::time_left_hours', { hours });
      } else if (minutes >= 1) {
        displayTime = ET('card_product::badge_sub::time_left_minutes', { minutes });
      } else {
        displayTime = ET('card_product::badge_sub::time_left_minutes', { minutes: 1 });
      }

      return html`<span class="${classes(klass.type_time_left, klass.size_medium, font('10_semibold'))}">
        <span class="${klass.count}">${html.preventEscape(displayTime)}</span>
      </span>`;
    } else if (props.type === 'Stock_left' && props.size === 'Medium (Medium~Small)') {
      const { displayAvailableStock } = props;

      return html`<span class="${classes(klass.type_stock_left, klass.size_medium, font('10_semibold'))}">
        ${ET('card_product::badge_sub::stock_left', { count: displayAvailableStock })}
      </span>`;
    } else if (props.type === 'Popular' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.type_popular, klass.size_medium, font('10_semibold'))}"
        >${ET('card_product::badge_sub::popular')}
      </span>`;
    } else if (props.type === 'Limited' && props.size === 'Medium (Medium~Small)') {
      const { quantity } = props;

      return html`<span class="${classes(klass.type_limited, klass.size_medium, font('10_semibold'))}"
        >${ET('card_product::badge_sub::limited', { quantity })}
      </span>`;
    } else if (props.type === 'Trend' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.type_trend, klass.size_medium, font('10_semibold'))}"
        >${ET('card_product::badge_sub::trend')}
      </span>`;
    } else {
      throw new CardProductError();
    }
  }
}
